// LLM Profile Management Module
// Handles CRUD operations for LLM profiles (ChatGPT, Mistral, etc.)

import { CONFIG } from '../../config.js';

const API_BASE = CONFIG.WS_API_BASE;

// Helper function to escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
}

// Show status message in profile form
function showProfileStatus(message, type) {
    const profileStatusMessage = document.getElementById('profileStatusMessage');
    if (profileStatusMessage) {
        profileStatusMessage.textContent = message;
        profileStatusMessage.className = `status-message ${type}`;
        if (type === 'success') {
            setTimeout(() => {
                profileStatusMessage.className = 'status-message';
            }, 3000);
        }
    }
}

// Fetch all profiles
export async function fetchProfiles() {
    const profileList = document.getElementById('profileList');

    try {
        // Fetch all profiles and active profile in parallel
        const [profilesResponse, activeResponse] = await Promise.all([
            fetch(`${API_BASE}/profiles`),
            fetch(`${API_BASE}/profiles/active`)
        ]);

        if (!profilesResponse.ok) throw new Error(`HTTP ${profilesResponse.status}`);
        const profiles = await profilesResponse.json();

        // Get active profile ID
        let activeProfileId = null;
        if (activeResponse.ok) {
            const activeProfile = await activeResponse.json();
            activeProfileId = activeProfile.id;
        }

        // Mark the active profile
        profiles.forEach(p => {
            p.is_active = (p.id === activeProfileId);
        });

        renderProfiles(profiles);
    } catch (error) {
        console.error('Error loading profiles:', error);
        profileList.innerHTML = `<div class="empty-state">${window.i18n?.t('settings.llmProfiles.loadError') || 'Error loading'}: ${error.message}</div>`;
    }
}

// Render profiles list
function renderProfiles(profiles) {
    const profileList = document.getElementById('profileList');

    if (!profiles || profiles.length === 0) {
        profileList.innerHTML = `<div class="empty-state">${window.i18n?.t('settings.llmProfiles.emptyState') || 'No profiles available'}</div>`;
        return;
    }

    // Ensure at least one profile is active (default to first if none)
    const hasActiveProfile = profiles.some(p => p.is_active);
    if (!hasActiveProfile && profiles.length > 0) {
        profiles[0].is_active = true;
    }

    profileList.innerHTML = profiles.map(profile => `
        <div class="profile-card ${profile.is_active ? 'active' : ''}" data-id="${profile.id}">
            <div class="profile-card-header">
                <span class="profile-name">
                    ${profile.is_active ? `<span class="active-label">✓ ${window.i18n?.t('common.active') || 'Active'}</span>` : ''}
                    ${escapeHtml(profile.name)}
                </span>
                <div class="profile-actions">
                    ${!profile.is_active ? `<button class="profile-btn success" onclick="activateProfile(${profile.id})">${window.i18n?.t('common.activate') || 'Activate'}</button>` : `<span class="active-indicator">${window.i18n?.t('common.inUse') || 'In Use'}</span>`}
                    <button class="profile-btn" onclick="editProfile(${profile.id})">${window.i18n?.t('common.edit') || 'Edit'}</button>
                    <button class="profile-btn danger" onclick="deleteProfile(${profile.id}, '${escapeHtml(profile.name)}')">${window.i18n?.t('common.delete') || 'Delete'}</button>
                </div>
            </div>
            <div class="profile-model">${window.i18n?.t('settings.llmProfiles.modelInfo', { model: escapeHtml(profile.model_name), api: escapeHtml(profile.api_url) }) || `${escapeHtml(profile.model_name)} - ${escapeHtml(profile.api_url)}`}</div>
        </div>
    `).join('');
}

// Show profile form for adding
function showProfileForm() {
    const profileForm = document.getElementById('profileForm');
    const profileFormTitle = document.getElementById('profileFormTitle');
    const addProfileBtn = document.getElementById('addProfileBtn');
    const profileEditId = document.getElementById('profileEditId');
    const profileName = document.getElementById('profileName');
    const profileApiUrl = document.getElementById('profileApiUrl');
    const profileApiKey = document.getElementById('profileApiKey');
    const profileModelName = document.getElementById('profileModelName');
    const profileStatusMessage = document.getElementById('profileStatusMessage');

    profileFormTitle.textContent = window.i18n?.t('settings.llmProfiles.newConnection') || 'New Connection';
    profileEditId.value = '';
    profileName.value = '';
    profileApiUrl.value = '';
    profileApiKey.value = '';
    profileModelName.value = '';
    profileStatusMessage.className = 'status-message';
    profileForm.classList.add('show');
    addProfileBtn.style.display = 'none';
}

// Hide profile form
function hideProfileForm() {
    const profileForm = document.getElementById('profileForm');
    const addProfileBtn = document.getElementById('addProfileBtn');

    profileForm.classList.remove('show');
    addProfileBtn.style.display = 'block';
}

// Edit an existing profile
export async function editProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/profiles`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const profiles = await response.json();
        const profile = profiles.find(p => p.id === id);

        if (!profile) {
            alert(window.i18n?.t('settings.llmProfiles.profileNotFound') || 'Profile not found!');
            return;
        }

        const profileForm = document.getElementById('profileForm');
        const profileFormTitle = document.getElementById('profileFormTitle');
        const addProfileBtn = document.getElementById('addProfileBtn');
        const profileEditId = document.getElementById('profileEditId');
        const profileName = document.getElementById('profileName');
        const profileApiUrl = document.getElementById('profileApiUrl');
        const profileApiKey = document.getElementById('profileApiKey');
        const profileModelName = document.getElementById('profileModelName');
        const profileStatusMessage = document.getElementById('profileStatusMessage');

        profileFormTitle.textContent = window.i18n?.t('settings.llmProfiles.editConnection') || 'Edit Connection';
        profileEditId.value = profile.id;
        profileName.value = profile.name;
        profileApiUrl.value = profile.api_url;
        profileApiKey.value = profile.api_key;
        profileModelName.value = profile.model_name;
        profileStatusMessage.className = 'status-message';
        profileForm.classList.add('show');
        addProfileBtn.style.display = 'none';
    } catch (error) {
        alert((window.i18n?.t('settings.llmProfiles.loadError') || 'Error loading profile') + ': ' + error.message);
    }
}

// Save profile (create or update)
async function saveProfile() {
    const profileName = document.getElementById('profileName');
    const profileApiUrl = document.getElementById('profileApiUrl');
    const profileApiKey = document.getElementById('profileApiKey');
    const profileModelName = document.getElementById('profileModelName');
    const profileEditId = document.getElementById('profileEditId');
    const saveProfileBtn = document.getElementById('saveProfileBtn');

    const name = profileName.value.trim();
    const apiUrl = profileApiUrl.value.trim();
    const apiKey = profileApiKey.value.trim();
    const modelName = profileModelName.value.trim();
    const editId = profileEditId.value;

    // Validation
    if (!name || !apiUrl || !apiKey || !modelName) {
        showProfileStatus(window.i18n?.t('settings.llmProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    saveProfileBtn.disabled = true;
    saveProfileBtn.textContent = window.i18n?.t('common.saving') || 'Saving...';

    try {
        let response;
        const profileData = {
            name: name,
            api_url: apiUrl,
            api_key: apiKey,
            model_name: modelName
        };

        if (editId) {
            // Update existing profile - delete and recreate since API doesn't have PUT
            await fetch(`${API_BASE}/profiles/${editId}`, { method: 'DELETE' });
            response = await fetch(`${API_BASE}/profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        } else {
            // Create new profile
            response = await fetch(`${API_BASE}/profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        }

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
                // If not JSON, try text
                const textError = await response.text();
                if (textError) errorMessage = textError;
            }
            throw new Error(errorMessage);
        }

        showProfileStatus(window.i18n?.t('settings.llmProfiles.saveSuccess') || '✓ Profile saved successfully!', 'success');
        setTimeout(() => {
            hideProfileForm();
            fetchProfiles();
        }, 800);

    } catch (error) {
        showProfileStatus((window.i18n?.t('settings.llmProfiles.saveError') || 'Error saving') + ': ' + error.message, 'error');
    } finally {
        saveProfileBtn.disabled = false;
        saveProfileBtn.textContent = window.i18n?.t('common.save') || 'Save';
    }
}

// Activate a profile
export async function activateProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/profiles/${id}/activate`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchProfiles(); // Reload the list
    } catch (error) {
        alert((window.i18n?.t('settings.llmProfiles.activateError') || 'Error activating') + ': ' + error.message);
    }
}

// Delete a profile
export async function deleteProfile(id, name) {
    if (!confirm(window.i18n?.t('settings.llmProfiles.deleteConfirm', { name: name }) || `Do you really want to delete profile "${name}"?`)) {
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/profiles/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchProfiles(); // Reload the list
    } catch (error) {
        alert((window.i18n?.t('settings.llmProfiles.deleteError') || 'Error deleting') + ': ' + error.message);
    }
}

// Initialize LLM profile module
export function initLLMProfiles() {
    const addProfileBtn = document.getElementById('addProfileBtn');
    const cancelProfileBtn = document.getElementById('cancelProfileBtn');
    const saveProfileBtn = document.getElementById('saveProfileBtn');
    const toggleApiKeyBtn = document.getElementById('toggleApiKey');
    const profileApiKey = document.getElementById('profileApiKey');

    // Event listeners
    if (addProfileBtn) {
        addProfileBtn.addEventListener('click', showProfileForm);
    }
    if (cancelProfileBtn) {
        cancelProfileBtn.addEventListener('click', hideProfileForm);
    }
    if (saveProfileBtn) {
        saveProfileBtn.addEventListener('click', saveProfile);
    }

    // Toggle API Key visibility
    if (toggleApiKeyBtn && profileApiKey) {
        toggleApiKeyBtn.addEventListener('click', () => {
            if (profileApiKey.type === 'password') {
                profileApiKey.type = 'text';
                toggleApiKeyBtn.textContent = '🙈';
            } else {
                profileApiKey.type = 'password';
                toggleApiKeyBtn.textContent = '👁️';
            }
        });
    }
}
